import {Dispatch} from "redux";

import {createFavouritesActionTypes, reduceFavouritesToDataType} from "@pg-mono/favourites";
import {IFavourite} from "@pg-mono/favourites";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IRootState} from "../../store/store";
import {selectGetIsUserAuthenticated} from "../../user/utils/select_get_is_user_authenticated";
import {fetchFavourites} from "../api/fetch_favourites";
import {getServerFavourite, updateFavourites} from "../api/update_favourites";
import {localStorageFavouritesService} from "./manage_local_storage_favourites";

const FAVOURITE = "favourite";
export const favouriteTypes = createFavouritesActionTypes(FAVOURITE);

const isErrorResponse = (error: unknown): error is {response: {status?: unknown}} => {
    return Object.prototype.hasOwnProperty.call(error, "response");
};

export const hydrateFavouritesFromApi = async (dispatch: Dispatch) => {
    try {
        const fetchedFavourites = await fetchFavourites({hide_deleted: true});

        if (fetchedFavourites.length > 0) {
            dispatch({type: favouriteTypes.set, list: fetchedFavourites, ...reduceFavouritesToDataType(fetchedFavourites)});
            return true;
        }
    } catch (error) {
        notifyBugsnag(
            {message: `hydrateFavouritesFromApi: ${isErrorResponse(error) ? error.response?.status : ""}`, filename: "manage_favourites.ts", error},
            "API fetch favourites error after update"
        );
    }

    return false;
};

export const addToFavourites =
    (favourites: IFavourite[], updateServerState = true, persistToLocalStorage = true, options?: {skipHydrate?: boolean}) =>
    async (dispatch: Dispatch, getState: () => IRootState) => {
        const favouriteByDataType = reduceFavouritesToDataType(favourites);

        const isAuthenticated = selectGetIsUserAuthenticated(getState());

        const favouritesList = [...favourites];

        if (isAuthenticated) {
            // During syncing local data with server state we don't need to update server state
            if (updateServerState) {
                try {
                    await updateFavourites(favourites.map((favourite) => getServerFavourite(favourite)));
                } catch (error) {
                    notifyBugsnag(
                        {message: `addToFavourites: ${isErrorResponse(error) ? error.response?.status : ""}`, filename: "manage_favourites.ts", error},
                        "API update favourites error during adding favourites"
                    );

                    return new Error(JSON.stringify(error));
                }

                if (!options?.skipHydrate && (await hydrateFavouritesFromApi(dispatch))) {
                    return true;
                }
            }
        } else if (persistToLocalStorage) {
            localStorageFavouritesService.addToStorageFavourites(favourites);
        }

        dispatch({type: favouriteTypes.add, list: favouritesList, ...favouriteByDataType});
        /*
         * NOTE: The result of this action will always be handled by `then` method - event if `Promise.reject()` will be returned.
         * In order to distinguish the type of action result inside the component we need to return `true` or some other value
         * than `instanceof Error`.
         */
        return true;
    };

export const addToComparison = (favourite: IFavourite, persistToLocalStorage = true, options?: {skipHydrate?: boolean}) =>
    updatePropertyComparison(favourite, true, persistToLocalStorage, options?.skipHydrate);

export const removeFromComparison = (favourite: IFavourite, persistToLocalStorage = true) => updatePropertyComparison(favourite, false, persistToLocalStorage);

const updatePropertyComparison =
    (favourite: IFavourite, inComparison: boolean, persistToLocalStorage = true, skipHydrate = false) =>
    async (dispatch: Dispatch, getState: () => IRootState) => {
        const favouriteWithComparison = {...favourite, in_comparison: inComparison};
        const isAuthenticated = selectGetIsUserAuthenticated(getState());
        const {list, offers, properties} = getState().favourite.favourites;
        const propertyId = favourite.property?.rp_id;
        const isAlreadyFavourite = propertyId != null && properties.includes(propertyId);

        if (isAuthenticated) {
            try {
                await updateFavourites([getServerFavourite(favouriteWithComparison)]);
            } catch (error) {
                notifyBugsnag(
                    {
                        message: `${inComparison ? "addToComparison" : "removeFromComparison"}: ${isErrorResponse(error) ? error.response?.status : ""}`,
                        filename: "manage_favourites.ts",
                        error
                    },
                    inComparison ? "API update favourites error during adding to comparison" : "API update favourites error during removing from comparison"
                );

                return false;
            }

            if (!skipHydrate && inComparison && !isAlreadyFavourite && (await hydrateFavouritesFromApi(dispatch))) {
                return true;
            }
        } else if (persistToLocalStorage && isAlreadyFavourite) {
            localStorageFavouritesService.removeFromStorageFavourites([favouriteWithComparison]);
            localStorageFavouritesService.addToStorageFavourites([favouriteWithComparison]);
        } else if (persistToLocalStorage && inComparison) {
            localStorageFavouritesService.addToStorageFavourites([favouriteWithComparison]);
        }

        if (isAlreadyFavourite) {
            dispatch({
                type: favouriteTypes.set,
                list: list.map((item) => (item.property?.rp_id === propertyId ? {...item, in_comparison: inComparison} : item)),
                offers,
                properties
            });
        } else if (inComparison) {
            dispatch({
                type: favouriteTypes.add,
                list: [favouriteWithComparison],
                ...reduceFavouritesToDataType([favouriteWithComparison])
            });
        }

        return true;
    };

export const removeFromFavourites =
    (favourites: IFavourite[], updateServerState = true) =>
    async (dispatch: Dispatch, getState: () => IRootState) => {
        const favouriteByDataType = reduceFavouritesToDataType(favourites);

        const isAuthenticated = selectGetIsUserAuthenticated(getState());

        let favouritesList = [...favourites];

        if (isAuthenticated) {
            // During syncing local data with server state we don't need to update server state
            if (updateServerState) {
                try {
                    favouritesList = await updateFavourites(favourites.map((favourite) => getServerFavourite(favourite, true)));
                } catch (error) {
                    notifyBugsnag(
                        {message: `removeFromFavourites: ${isErrorResponse(error) ? error.response?.status : ""}`, filename: "manage_favourites.ts", error},
                        "API update favourites error during removing favourites"
                    );

                    return new Error(JSON.stringify(error));
                }
            }
        } else {
            localStorageFavouritesService.removeFromStorageFavourites(favourites);
        }

        dispatch({type: favouriteTypes.remove, list: favouritesList, ...favouriteByDataType});
        /*
         * NOTE: The result of this action will always be handled by `then` method - event if `Promise.reject()` will be returned.
         * In order to distinguish the type of action result inside the component we need to return `true` or some other value
         * than `instanceof Error`.
         */
        return true;
    };
import {Dispatch} from "redux";

import {createFavouritesActionTypes, reduceFavouritesToDataType} from "@pg-mono/favourites";
import {IFavourite} from "@pg-mono/favourites";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IRootState} from "../../store/store";
import {selectGetIsUserAuthenticated} from "../../user/utils/select_get_is_user_authenticated";
import {fetchFavourites} from "../api/fetch_favourites";
import {getServerFavourite, updateFavourites} from "../api/update_favourites";
import {localStorageFavouritesService} from "./manage_local_storage_favourites";

const FAVOURITE = "favourite";
export const favouriteTypes = createFavouritesActionTypes(FAVOURITE);

const isErrorResponse = (error: unknown): error is {response: {status?: unknown}} => {
    return Object.prototype.hasOwnProperty.call(error, "response");
};

export const hydrateFavouritesFromApi = async (dispatch: Dispatch) => {
    try {
        const fetchedFavourites = await fetchFavourites({hide_deleted: true});

        if (fetchedFavourites.length > 0) {
            dispatch({type: favouriteTypes.set, list: fetchedFavourites, ...reduceFavouritesToDataType(fetchedFavourites)});
            return true;
        }
    } catch (error) {
        notifyBugsnag(
            {message: `hydrateFavouritesFromApi: ${isErrorResponse(error) ? error.response?.status : ""}`, filename: "manage_favourites.ts", error},
            "API fetch favourites error after update"
        );
    }

    return false;
};

export const addToFavourites =
    (favourites: IFavourite[], updateServerState = true, persistToLocalStorage = true, options?: {skipHydrate?: boolean}) =>
    async (dispatch: Dispatch, getState: () => IRootState) => {
        const favouriteByDataType = reduceFavouritesToDataType(favourites);

        const isAuthenticated = selectGetIsUserAuthenticated(getState());

        const favouritesList = [...favourites];

        if (isAuthenticated) {
            // During syncing local data with server state we don't need to update server state
            if (updateServerState) {
                try {
                    await updateFavourites(favourites.map((favourite) => getServerFavourite(favourite)));
                } catch (error) {
                    notifyBugsnag(
                        {message: `addToFavourites: ${isErrorResponse(error) ? error.response?.status : ""}`, filename: "manage_favourites.ts", error},
                        "API update favourites error during adding favourites"
                    );

                    return new Error(JSON.stringify(error));
                }

                if (!options?.skipHydrate && (await hydrateFavouritesFromApi(dispatch))) {
                    return true;
                }
            }
        } else if (persistToLocalStorage) {
            localStorageFavouritesService.addToStorageFavourites(favourites);
        }

        dispatch({type: favouriteTypes.add, list: favouritesList, ...favouriteByDataType});
        /*
         * NOTE: The result of this action will always be handled by `then` method - event if `Promise.reject()` will be returned.
         * In order to distinguish the type of action result inside the component we need to return `true` or some other value
         * than `instanceof Error`.
         */
        return true;
    };

export const addToComparison = (favourite: IFavourite, persistToLocalStorage = true, options?: {skipHydrate?: boolean}) =>
    updatePropertyComparison(favourite, true, persistToLocalStorage, options?.skipHydrate);

export const removeFromComparison = (favourite: IFavourite, persistToLocalStorage = true) => updatePropertyComparison(favourite, false, persistToLocalStorage);

const updatePropertyComparison =
    (favourite: IFavourite, inComparison: boolean, persistToLocalStorage = true, skipHydrate = false) =>
    async (dispatch: Dispatch, getState: () => IRootState) => {
        const favouriteWithComparison = {...favourite, in_comparison: inComparison};
        const isAuthenticated = selectGetIsUserAuthenticated(getState());
        const {list, offers, properties} = getState().favourite.favourites;
        const propertyId = favourite.property?.rp_id;
        const isAlreadyFavourite = propertyId != null && properties.includes(propertyId);

        if (isAuthenticated) {
            try {
                await updateFavourites([getServerFavourite(favouriteWithComparison)]);
            } catch (error) {
                notifyBugsnag(
                    {
                        message: `${inComparison ? "addToComparison" : "removeFromComparison"}: ${isErrorResponse(error) ? error.response?.status : ""}`,
                        filename: "manage_favourites.ts",
                        error
                    },
                    inComparison ? "API update favourites error during adding to comparison" : "API update favourites error during removing from comparison"
                );

                return false;
            }

            if (!skipHydrate && inComparison && !isAlreadyFavourite && (await hydrateFavouritesFromApi(dispatch))) {
                return true;
            }
        } else if (persistToLocalStorage && isAlreadyFavourite) {
            localStorageFavouritesService.removeFromStorageFavourites([favouriteWithComparison]);
            localStorageFavouritesService.addToStorageFavourites([favouriteWithComparison]);
        } else if (persistToLocalStorage && inComparison) {
            localStorageFavouritesService.addToStorageFavourites([favouriteWithComparison]);
        }

        if (isAlreadyFavourite) {
            dispatch({
                type: favouriteTypes.set,
                list: list.map((item) => (item.property?.rp_id === propertyId ? {...item, in_comparison: inComparison} : item)),
                offers,
                properties
            });
        } else if (inComparison) {
            dispatch({
                type: favouriteTypes.add,
                list: [favouriteWithComparison],
                ...reduceFavouritesToDataType([favouriteWithComparison])
            });
        }

        return true;
    };

export const removeFromFavourites =
    (favourites: IFavourite[], updateServerState = true) =>
    async (dispatch: Dispatch, getState: () => IRootState) => {
        const favouriteByDataType = reduceFavouritesToDataType(favourites);

        const isAuthenticated = selectGetIsUserAuthenticated(getState());

        let favouritesList = [...favourites];

        if (isAuthenticated) {
            // During syncing local data with server state we don't need to update server state
            if (updateServerState) {
                try {
                    favouritesList = await updateFavourites(favourites.map((favourite) => getServerFavourite(favourite, true)));
                } catch (error) {
                    notifyBugsnag(
                        {message: `removeFromFavourites: ${isErrorResponse(error) ? error.response?.status : ""}`, filename: "manage_favourites.ts", error},
                        "API update favourites error during removing favourites"
                    );

                    return new Error(JSON.stringify(error));
                }
            }
        } else {
            localStorageFavouritesService.removeFromStorageFavourites(favourites);
        }

        dispatch({type: favouriteTypes.remove, list: favouritesList, ...favouriteByDataType});
        /*
         * NOTE: The result of this action will always be handled by `then` method - event if `Promise.reject()` will be returned.
         * In order to distinguish the type of action result inside the component we need to return `true` or some other value
         * than `instanceof Error`.
         */
        return true;
    };
